import { createPortal } from "react-dom";
import type { CustomButtonInput, DatesSetArg, ToolbarInput } from "@fullcalendar/core";
import deLocale from "@fullcalendar/core/locales/de";
import FullCalendar from "@fullcalendar/react";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, IconButton, ListItemIcon, Menu, MenuItem, useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material";
import coursesYearViewPlugin from "../../misc/calendar/coursesYearViewPlugin";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Form, SelectInput, useGetList, useRecordContext } from "react-admin";
import { AddAppointmentDialog } from "../../dialogs/AddAppointmentDialog/AddAppointmentDialog";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import { useDateFromHashOrNow } from "../../hooks/useDateFromHashOrNow";
import { useDialog } from "../../hooks/useDialog";
import { grants } from "../../backoffice.access_control";
import { DrivingSchool } from "../../providers/drivingSchoolsProvider";
import { CreateCourseDialog } from "../../dialogs/CreateCourseDialog";
import { Branch } from "../../providers/branchesProvider";
import {
  persistentCalendarViewStateFamily,
  persistentDrivingSchoolCoursesCalendarFiltersStateFamily,
} from "../../persistent_state";
import { useRecoilState } from "recoil";
import { useFullcalendarCustomButtonAnchor } from "../../hooks/useFullcalendarCustomButtonAnchor";
import { useMenu } from "../../hooks/useMenu";

const _ALL_BRANCHES = "__all_branches__";
const _ALL_COURSE_TYPES = "__all_course_types__";
const _ALL_THEORY_COURSE_TYPES = "__all_theory_course_types__";

type CoursesCalendarView = "yearView" | "quarterView";

const calendarViewChoices = [
  { id: "yearView", name: "Jahresansicht" },
  { id: "quarterView", name: "Quartalsansicht" },
] as const satisfies Array<{ id: CoursesCalendarView; name: string }>;

interface DrivingSchoolCoursesCalendarContext {
  setCourseTypes: (courseTypes: Array<string>) => void;
  setTheoryCourseTypes: (theoryCourseTypes: Array<string>) => void;
  selectedCourseType?: string;
  selectedTheoryCourseType?: string;
  selectedBranchId?: string;
}

const _DrivingSchoolCoursesCalendarContext = createContext<DrivingSchoolCoursesCalendarContext>({
  setCourseTypes: () => {
    throw new Error("No <_DrivingSchoolCoursesCalendarContext.Provider> ancestor node");
  },
  setTheoryCourseTypes: () => {
    throw new Error("No <_DrivingSchoolCoursesCalendarContext.Provider> ancestor node");
  },
});

export function useDrivingSchoolCoursesCalendarContext(): DrivingSchoolCoursesCalendarContext {
  return useContext(_DrivingSchoolCoursesCalendarContext);
}

export function DrivingSchoolCoursesCalendar() {
  const drivingSchool = useRecordContext<DrivingSchool>();
  const drivingSchoolId = drivingSchool?.id;
  const { data: activeBranches } = useGetList<Branch>(
    "branches",
    {
      filter: { drivingSchoolId },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: !!drivingSchoolId },
  );

  if (!drivingSchool || !activeBranches) {
    return <LoadingIndicator />;
  }

  return (
    <_DrivingSchoolCoursesCalendar key={drivingSchool.id} drivingSchool={drivingSchool} branches={activeBranches} />
  );
}

function _DrivingSchoolCoursesCalendar(props: { drivingSchool: DrivingSchool; branches: Branch[] }) {
  const { drivingSchool, branches } = props;
  ////////////////////////////////////////////////////////////////
  // We need to memoize *all* properties given to FullCalendar, //
  // to prevent rerendering and therefore flickering ...        //
  ////////////////////////////////////////////////////////////////
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const fullCalendarRef = useRef<FullCalendar>(null);
  const initialDate = useDateFromHashOrNow();
  const [title, setTitle] = useState("");

  const [courseTypes, setCourseTypes] = useState<Array<string>>([]);
  const courseTypeChoices = useMemo(() => courseTypes.map((it) => ({ id: it, name: it })), [courseTypes]);
  const showCourseTypeDropdown = courseTypes.length > 1;
  const [persistedFilters, setPersistedFilters] = useRecoilState(
    persistentDrivingSchoolCoursesCalendarFiltersStateFamily(drivingSchool.id),
  );
  const selectedCourseType = persistedFilters.selectedCourseType ?? _ALL_COURSE_TYPES;
  const setSelectedCourseType = useCallback(
    (selectedCourseType: string) => setPersistedFilters((filters) => ({ ...filters, selectedCourseType })),
    [setPersistedFilters],
  );
  useEffect(() => {
    if (
      courseTypes.length > 0 &&
      selectedCourseType !== _ALL_COURSE_TYPES &&
      !courseTypes.includes(selectedCourseType)
    ) {
      setSelectedCourseType(_ALL_COURSE_TYPES);
    }
  }, [courseTypes, selectedCourseType]);

  const [theoryCourseTypes, setTheoryCourseTypes] = useState<Array<string>>([]);
  const theoryCourseTypeChoices = useMemo(
    () => theoryCourseTypes.map((it) => ({ id: it, name: it })),
    [theoryCourseTypes],
  );
  const selectedTheoryCourseType = persistedFilters.selectedTheoryCourseType ?? _ALL_THEORY_COURSE_TYPES;
  const setSelectedTheoryCourseType = useCallback(
    (selectedTheoryCourseType: string) => setPersistedFilters((filters) => ({ ...filters, selectedTheoryCourseType })),
    [setPersistedFilters],
  );
  const showTheoryCourseTypeDropdown =
    (selectedCourseType === "Theoriekurs" || !showCourseTypeDropdown) && theoryCourseTypes.length > 1;
  useEffect(() => {
    if (
      theoryCourseTypes.length > 0 &&
      selectedTheoryCourseType !== _ALL_THEORY_COURSE_TYPES &&
      !theoryCourseTypes.includes(selectedTheoryCourseType)
    ) {
      setSelectedTheoryCourseType(_ALL_THEORY_COURSE_TYPES);
    }
  }, [theoryCourseTypes, selectedTheoryCourseType]);

  const showBranchDropdown = branches.length > 1;
  const selectedBranchId = persistedFilters.selectedBranchId ?? _ALL_BRANCHES;
  const setSelectedBranchId = useCallback(
    (selectedBranchId: string) => setPersistedFilters((filters) => ({ ...filters, selectedBranchId })),
    [setPersistedFilters],
  );
  useEffect(() => {
    if (selectedBranchId !== _ALL_BRANCHES && !branches.some((it) => it.id === selectedBranchId)) {
      setSelectedBranchId(_ALL_BRANCHES);
    }
  }, [branches, selectedBranchId]);

  const contextValue = useMemo(
    () => ({
      setCourseTypes,
      setTheoryCourseTypes,
      selectedCourseType: selectedCourseType === _ALL_COURSE_TYPES ? "" : selectedCourseType,
      selectedTheoryCourseType: selectedTheoryCourseType === _ALL_THEORY_COURSE_TYPES ? "" : selectedTheoryCourseType,
      selectedBranchId: selectedBranchId === _ALL_BRANCHES ? "" : selectedBranchId,
    }),
    [selectedCourseType, selectedTheoryCourseType, selectedBranchId],
  );

  const [persistedView, setPersistedView] = useRecoilState(
    persistentCalendarViewStateFamily("DrivingSchoolCoursesCalendar"),
  );
  const courseFiltersAnchor = useFullcalendarCustomButtonAnchor("courseFiltersAnchor");
  // We do not determine the events to be shown here, but in the CoursesYearView, to prevent
  // that the FullCalendar is rerendered which leads to flickering ...
  const events = useMemo(() => [], []);
  const { dialogProps: newCourseDialogProps, openDialog: openNewCourseDialog } = useDialog("NewCourseDialog");
  const { dialogProps: newLessonDialogProps, openDialog: openNewLessonDialog } = useDialog("NewLessonDialog");
  const plugins = useMemo(() => [coursesYearViewPlugin], []);
  const locales = useMemo(() => [deLocale], []);
  const canCreateCourse =
    grants.has("theoryCoursesCreate") || grants.has("TheoryCourse:create") || grants.has("Course:create");
  const canCreateLesson = grants.has("TheoryLesson:create");
  const headerToolbar = useMemo<ToolbarInput>(
    () => ({
      left: "courseFiltersAnchor",
      center: "",
      right: isMobile
        ? "prev today next"
        : `prev today next${canCreateCourse ? " addCourseButton" : ""}${canCreateLesson ? " addAppointmentButton" : ""} yearView,quarterView`,
    }),
    [canCreateCourse, canCreateLesson, isMobile],
  );
  const customButtons = useMemo<Record<string, CustomButtonInput>>(
    () => ({
      addCourseButton: { text: "＋\u00A0Kurs", click: openNewCourseDialog, hint: "Neuen Kurs hinzufügen" },
      addAppointmentButton: { text: "＋\u00A0Termin", click: openNewLessonDialog, hint: "Neuen Termin hinzufügen" },
      courseFiltersAnchor: {},
    }),
    [openNewCourseDialog, openNewLessonDialog],
  );
  const buttonText = useMemo(() => ({ yearView: "Jahr", quarterView: "Quartal" }), []);
  const onDatesSet = useCallback(
    (dates: DatesSetArg) => {
      setTitle(dates.view.title);
      if (dates.view.type && persistedView !== dates.view.type) {
        setPersistedView(dates.view.type);
      }
    },
    [persistedView],
  );
  const activeCalendarView: CoursesCalendarView = persistedView === "quarterView" ? "quarterView" : "yearView";
  const changeCalendarView = useCallback((view: CoursesCalendarView) => {
    fullCalendarRef.current?.getApi().changeView(view);
  }, []);

  return (
    <Box
      data-testid="DrivingSchoolCoursesCalendar"
      className="courses-calendar"
      sx={{
        "& .fc-header-toolbar": { marginTop: "18px" },
        "& .fc-header-toolbar .fc-toolbar-chunk > .fc-courseFiltersAnchor-anchor": {
          marginLeft: "0 !important",
        },
        ...(isMobile
          ? {
              minWidth: 0,
              width: "100%",
              "& .fc-header-toolbar": { alignItems: "center", flexWrap: "wrap", gap: 1.5, marginTop: "18px" },
              "& .fc-header-toolbar .fc-toolbar-chunk:first-of-type": { width: "100%" },
              "& .fc-header-toolbar .fc-toolbar-chunk:last-of-type": {
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                width: "100%",
              },
              "& .fc-header-toolbar .fc-toolbar-chunk:last-of-type > .fc-button": { marginLeft: 0 },
              "& .fc-courseFiltersAnchor-anchor": { marginLeft: "0 !important", width: "100%" },
              "& .fc-courseFiltersAnchor-anchor form": { width: "100%" },
            }
          : {}),
      }}
    >
      <Box sx={{ alignItems: "center", display: "flex", gap: 1.5, justifyContent: "space-between" }}>
        <h2 className="fc-toolbar-title" style={{ fontSize: "1.25em", lineHeight: "32px", margin: 0, minWidth: 0 }}>
          {title}
        </h2>
        {isMobile && (
          <Box sx={{ alignItems: "center", display: "flex" }}>
            {(canCreateCourse || canCreateLesson) && (
              <CoursesCalendarCreateMenuButton
                canCreateCourse={canCreateCourse}
                canCreateLesson={canCreateLesson}
                openNewCourseDialog={openNewCourseDialog}
                openNewLessonDialog={openNewLessonDialog}
              />
            )}
            <CoursesCalendarHeaderMenuButton
              activeCalendarView={activeCalendarView}
              changeCalendarView={changeCalendarView}
            />
          </Box>
        )}
      </Box>
      <_DrivingSchoolCoursesCalendarContext.Provider value={contextValue}>
        <FullCalendar
          ref={fullCalendarRef}
          timeZone="Europe/Berlin"
          locales={locales}
          height="auto"
          plugins={plugins}
          headerToolbar={headerToolbar}
          buttonText={buttonText}
          customButtons={customButtons}
          initialDate={initialDate.toISO()}
          initialView={activeCalendarView}
          editable={false}
          nowIndicator={true}
          events={events}
          datesSet={onDatesSet}
        />
      </_DrivingSchoolCoursesCalendarContext.Provider>
      <CreateCourseDialog {...newCourseDialogProps} />
      <AddAppointmentDialog {...newLessonDialogProps} tabs={["theoryLesson"]} />
      {courseFiltersAnchor &&
        (showCourseTypeDropdown || showTheoryCourseTypeDropdown || showBranchDropdown) &&
        (createPortal(
          <Form
            key={`${selectedCourseType}-${selectedTheoryCourseType}-${selectedBranchId}`}
            defaultValues={{ selectedCourseType, selectedTheoryCourseType, selectedBranchId }}
          >
            <div
              className="fc-course-filters"
              style={isMobile ? { alignItems: "stretch", flexDirection: "column", width: "100%" } : undefined}
            >
              {showCourseTypeDropdown && (
                <SelectInput
                  className="filter-field"
                  source="selectedCourseType"
                  label="Kursart"
                  emptyText="Alle Kursarten"
                  emptyValue={_ALL_COURSE_TYPES}
                  choices={courseTypeChoices}
                  helperText={false}
                  disabled={courseTypeChoices.length === 0}
                  fullWidth={isMobile}
                  onChange={(event) => setSelectedCourseType(event.target.value)}
                />
              )}
              {showTheoryCourseTypeDropdown && (
                <SelectInput
                  className="filter-field"
                  source="selectedTheoryCourseType"
                  label="Theoriekurs"
                  emptyText="Alle Theoriekurse"
                  emptyValue={_ALL_THEORY_COURSE_TYPES}
                  choices={theoryCourseTypeChoices}
                  helperText={false}
                  disabled={theoryCourseTypes.length === 0}
                  fullWidth={isMobile}
                  onChange={(event) => setSelectedTheoryCourseType(event.target.value)}
                />
              )}
              {showBranchDropdown && (
                <SelectInput
                  className="filter-field"
                  source="selectedBranchId"
                  label="Filiale"
                  emptyText="Alle Filialen"
                  emptyValue={_ALL_BRANCHES}
                  choices={branches}
                  helperText={false}
                  fullWidth={isMobile}
                  onChange={(event) => setSelectedBranchId(event.target.value)}
                />
              )}
            </div>
          </Form>,
          courseFiltersAnchor,
        ) as any)}
    </Box>
  );
}

function CoursesCalendarCreateMenuButton(props: {
  canCreateCourse: boolean;
  canCreateLesson: boolean;
  openNewCourseDialog: () => void;
  openNewLessonDialog: () => void;
}) {
  const { canCreateCourse, canCreateLesson, openNewCourseDialog, openNewLessonDialog } = props;
  const { anchorEl, open, openMenu, closeMenu } = useMenu();

  const selectAction = (action: () => void) => {
    closeMenu();
    action();
  };

  return (
    <div>
      <IconButton size="small" onClick={openMenu} aria-label="Kurs oder Termin hinzufügen">
        <AddIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
        {canCreateCourse && (
          <MenuItem onClick={() => selectAction(openNewCourseDialog)}>
            <ListItemIcon>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            Kurs
          </MenuItem>
        )}
        {canCreateLesson && (
          <MenuItem onClick={() => selectAction(openNewLessonDialog)}>
            <ListItemIcon>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            Termin
          </MenuItem>
        )}
      </Menu>
    </div>
  );
}

function CoursesCalendarHeaderMenuButton(props: {
  activeCalendarView: CoursesCalendarView;
  changeCalendarView: (view: CoursesCalendarView) => void;
}) {
  const { activeCalendarView, changeCalendarView } = props;
  const { anchorEl, open, openMenu, closeMenu } = useMenu();

  return (
    <div>
      <IconButton size="small" onClick={openMenu} aria-label="Kurskalender-Menü öffnen">
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
        {calendarViewChoices.map((choice) => (
          <MenuItem
            key={choice.id}
            selected={activeCalendarView === choice.id}
            onClick={() => {
              closeMenu();
              changeCalendarView(choice.id);
            }}
          >
            {choice.name}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}
